import React from "react";

// その日の訪問者ごとの行動。匿名の訪問者 ID は日付が変わると別の番号になるので、1 日の中だけを見る
// （プライバシーポリシー「アクセス情報の取得と利用」の説明どおり。個人の特定・日をまたいだ追跡はしない）

export interface VisitorRow {
  visitor: string;
  first: string;
  last: string;
  pageviews: number;
  pages: number;
  clicks: number;
  landing: string;
  referrer: string;
  device: string;
  ip: string;
  tel: number;
  line: number;
  form: number;
}

export interface VisitorDetail {
  visitor: string;
  ip: string;
  device: string;
  user_agent: string;
  pages: { key: string; count: number }[];
  timeline: {
    at: string;
    kind: string;
    path: string;
    target: string;
    label: string;
    href: string;
    referrer: string;
    scroll: number;
  }[];
}

interface Props {
  visitors: VisitorRow[];
  selected: string | null;
  detail: VisitorDetail | null;
  onSelect: (visitor: string) => void;
}

const DEVICE_LABEL: Record<string, string> = { pc: "パソコン", sp: "スマホ", tablet: "タブレット" };
const TARGET_LABEL: Record<string, string> = {
  tel: "電話",
  line: "LINE",
  mail: "メール",
  map: "地図",
  external: "外部リンク",
  internal: "ページ移動",
  button: "ボタン",
  form: "フォーム",
};

const time = (s: string) =>
  new Date(s).toLocaleTimeString("ja-JP", { timeZone: "Asia/Tokyo", hour: "2-digit", minute: "2-digit", second: "2-digit" });
const fmt = (n: number) => n.toLocaleString("ja-JP");
const shortID = (v: string) => v.slice(0, 8);
const sourceOf = (ref: string) => (ref === "" ? "（直接）" : ref.startsWith("/") ? `サイト内 ${ref}` : ref);

export const VisitorsView: React.FC<Props> = ({ visitors, selected, detail, onSelect }) => (
  <div className="an-visitors">
    <section className="an-card">
      <p className="an-muted">
        訪問者 ID は、IP アドレス・ブラウザの種類・その日だけの乱数から作った番号です。同じ日の中の行動をまとめて見るためのもので、日付が変わると別の番号になります。
      </p>
      {visitors.length === 0 ? (
        <p className="an-muted">この日の訪問はまだありません。</p>
      ) : (
        <table className="an-table">
          <thead>
            <tr>
              <th>最後に見た時刻</th>
              <th>訪問者 ID</th>
              <th className="num">閲覧数</th>
              <th className="num">ページ数</th>
              <th>入口</th>
              <th>参照元</th>
              <th>端末</th>
              <th>IP アドレス</th>
              <th>問い合わせ行動</th>
            </tr>
          </thead>
          <tbody>
            {visitors.map((v) => (
              <tr
                key={v.visitor}
                className={selected === v.visitor ? "an-row-selected" : undefined}
                tabIndex={0}
                onClick={() => onSelect(v.visitor)}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onSelect(v.visitor)}
                aria-selected={selected === v.visitor}
              >
                <td className="num">{time(v.last)}</td>
                <td className="num">{shortID(v.visitor)}</td>
                <td className="num">{fmt(v.pageviews)}</td>
                <td className="num">{fmt(v.pages)}</td>
                <td className="an-ellipsis">{v.landing || "-"}</td>
                <td className="an-ellipsis">{sourceOf(v.referrer)}</td>
                <td>{DEVICE_LABEL[v.device] ?? v.device}</td>
                <td className="num">{v.ip || "-"}</td>
                <td>
                  {v.tel > 0 && <span className="an-tag an-tag-tel">電話 {v.tel}</span>}{" "}
                  {v.line > 0 && <span className="an-tag an-tag-line">LINE {v.line}</span>}{" "}
                  {v.form > 0 && <span className="an-tag an-tag-form">フォーム {v.form}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>

    {selected && detail && (
      <section className="an-card" aria-label="訪問者の行動">
        <h2>
          訪問者 {shortID(detail.visitor)} の行動
          <span className="an-muted" style={{ fontWeight: 400, marginLeft: 8 }}>
            {DEVICE_LABEL[detail.device] ?? detail.device}・{detail.ip || "IP は保存期間を過ぎたため削除済み"}
          </span>
        </h2>
        {detail.user_agent && <p className="an-muted an-ellipsis">{detail.user_agent}</p>}
        <div className="an-visitor-detail">
          <div>
            <h3>ページ別の閲覧回数</h3>
            <table className="an-table">
              <thead>
                <tr>
                  <th>ページ</th>
                  <th className="num">回数</th>
                </tr>
              </thead>
              <tbody>
                {detail.pages.map((p) => (
                  <tr key={p.key}>
                    <td className="an-ellipsis">{p.key}</td>
                    <td className="num">{fmt(p.count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <h3>時系列</h3>
            <ol className="an-timeline">
              {detail.timeline.map((t, i) => (
                <li key={i} className={`an-timeline-${t.kind}`}>
                  <span className="an-timeline-time">{time(t.at)}</span>
                  {t.kind === "pageview" && (
                    <span>
                      <strong>{t.path}</strong> を表示
                      <span className="an-muted">
                        {" "}
                        （{t.referrer === "" ? "直接の訪問" : `${sourceOf(t.referrer)} から`}
                        {t.scroll > 0 ? `・${t.scroll}% までスクロール` : ""}）
                      </span>
                    </span>
                  )}
                  {t.kind === "click" && (
                    <span>
                      <span className={`an-tag an-tag-${t.target || "other"}`}>{TARGET_LABEL[t.target] ?? "クリック"}</span>{" "}
                      {t.label || <span className="an-muted">（文字なし）</span>}
                      {t.href && <span className="an-muted"> → {t.href}</span>}
                    </span>
                  )}
                  {t.kind === "conversion" && (
                    <span>
                      <span className="an-tag an-tag-form">フォーム送信</span> {t.path} からお問い合わせ
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>
    )}
  </div>
);
